import { chooseItemPriceBasis } from './itemPriceTimeline.mjs';

const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asTime = (value) => {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const itemKey = (value) => String(value ?? '');

const pricePair = (row, basis) => ({
  oldPrice: asNumber(row?.[`old_${basis}_price`]),
  newPrice: asNumber(row?.[`new_${basis}_price`]),
});

const isActualChange = ({ oldPrice, newPrice }) => (
  oldPrice !== null
  && newPrice !== null
  && oldPrice > 0
  && oldPrice !== newPrice
);

const normalizeLabel = (value, fallback) => String(value || '').trim() || fallback;

export const PRICE_CHANGE_HISTORY_SELECT = [
  'id',
  'inventory_item_id',
  'changed_at',
  'change_type',
  'old_case_price',
  'new_case_price',
  'old_unit_price',
  'new_unit_price',
  'source',
  'new_name',
  'old_name',
  'invoice_vendor',
  'invoice_number',
].join(',');

export const PRICE_CHANGE_ITEM_SELECT = [
  'id',
  'description',
  'vendor',
  'category',
  'location',
  'created_at',
  'case_price',
  'unit_price',
].join(',');

/** Load the store's inventory-category → P&L-category names from the normalized
 * tables. pnl_category_mappings stores an id, not a denormalized name. */
export async function loadPriceChangeCategoryMappings({ supabase, storeId }) {
  const [mappingsRes, categoriesRes] = await Promise.all([
    supabase
      .from('pnl_category_mappings')
      .select('inventory_category,pnl_category_id')
      .eq('store_id', storeId),
    supabase
      .from('pnl_categories')
      .select('id,name')
      .eq('store_id', storeId),
  ]);
  if (mappingsRes.error) throw mappingsRes.error;
  if (categoriesRes.error) throw categoriesRes.error;
  const namesById = new Map((categoriesRes.data || []).map((row) => [String(row.id), row.name || '']));
  return (mappingsRes.data || []).map((row) => ({
    inventory_category: row.inventory_category,
    pnl_category: namesById.get(String(row.pnl_category_id)) || '',
  }));
}

export function summarizePriceChangeGroups(rows = [], field) {
  const fallback = field === 'vendor' ? 'No vendor' : 'Uncategorized';
  const groups = new Map();

  rows.forEach((row) => {
    const label = normalizeLabel(row?.[field], fallback);
    const current = groups.get(label) || {
      label,
      itemCount: 0,
      increaseCount: 0,
      decreaseCount: 0,
      totalDollarChange: 0,
      totalMovement: 0,
    };
    current.itemCount += 1;
    current.totalDollarChange += row.absChange || 0;
    current.totalMovement += row.grossDollarChange || Math.abs(row.absChange || 0);
    if ((row.absChange || 0) > 0) current.increaseCount += 1;
    if ((row.absChange || 0) < 0) current.decreaseCount += 1;
    groups.set(label, current);
  });

  return [...groups.values()].sort((left, right) => (
    right.totalMovement - left.totalMovement
    || Math.abs(right.totalDollarChange) - Math.abs(left.totalDollarChange)
    || left.label.localeCompare(right.label)
  ));
}
/**
 * Convert durable price_history events into report rows. Only actual price
 * movements inside the selected range are included; catalog setup, renames,
 * and events outside the window cannot make an item appear changed.
 */
export function buildPriceChangeReportData({
  historyRows = [],
  inventoryItems = [],
  categoryMappings = [],
  startDate,
  endDate,
} = {}) {
  const startTime = asTime(`${startDate}T00:00:00`);
  const endTime = asTime(`${endDate}T23:59:59.999`);
  if (startTime === null || endTime === null || startTime > endTime) {
    throw new Error('Choose a valid price-change date range.');
  }

  const itemsById = new Map(inventoryItems.map((item) => [itemKey(item.id), item]));
  const pnlByCategory = new Map(categoryMappings.map((mapping) => [
    String(mapping.inventory_category || '').trim().toLowerCase(),
    mapping.pnl_category || '',
  ]));
  const historyByItem = new Map();

  historyRows.forEach((row) => {
    if (row?.inventory_item_id === null || row?.inventory_item_id === undefined) return;
    const key = itemKey(row.inventory_item_id);
    if (!historyByItem.has(key)) historyByItem.set(key, []);
    historyByItem.get(key).push(row);
  });

  const rows = [];
  historyByItem.forEach((itemHistory, key) => {
    const sortedHistory = itemHistory
      .filter((row) => asTime(row.changed_at) !== null)
      .slice()
      .sort((left, right) => asTime(left.changed_at) - asTime(right.changed_at));
    const item = itemsById.get(key) || null;
    const basis = chooseItemPriceBasis(item, sortedHistory);
    const changes = sortedHistory
      .filter((row) => {
        const time = asTime(row.changed_at);
        return time >= startTime && time <= endTime && isActualChange(pricePair(row, basis));
      })
      .map((row) => ({ ...row, ...pricePair(row, basis) }));

    if (!changes.length) return;

    const first = changes[0];
    const last = changes[changes.length - 1];
    const startPrice = first.oldPrice;
    const endPrice = last.newPrice;
    const absChange = endPrice - startPrice;
    const pctChange = startPrice > 0 ? (absChange / Math.abs(startPrice)) * 100 : null;
    const grossDollarChange = changes.reduce(
      (sum, change) => sum + Math.abs(change.newPrice - change.oldPrice),
      0,
    );
    const category = item?.category || '';

    rows.push({
      inventoryItemId: item?.id ?? first.inventory_item_id,
      itemName: item?.description || last.new_name || first.old_name || '(unknown item)',
      vendor: item?.vendor || last.invoice_vendor || '',
      category,
      pnlCategory: category ? (pnlByCategory.get(category.trim().toLowerCase()) || '') : '',
      location: item?.location || '',
      basis,
      startPrice,
      endPrice,
      absChange,
      pctChange,
      grossDollarChange,
      changeCount: changes.length,
      firstChangedAt: first.changed_at,
      lastChangedAt: last.changed_at,
      latestSource: last.source || '',
      latestInvoiceNumber: last.invoice_number || '',
      item: item || {
        id: first.inventory_item_id,
        description: item?.description || last.new_name || first.old_name || '(unknown item)',
        vendor: item?.vendor || last.invoice_vendor || '',
        category,
      },
      historyRows: sortedHistory,
    });
  });

  rows.sort((left, right) => left.itemName.localeCompare(right.itemName));
  const vendors = summarizePriceChangeGroups(rows, 'vendor');
  const categories = summarizePriceChangeGroups(rows, 'category');
  const biggestItem = [...rows].sort((left, right) => (
    Math.abs(right.absChange) - Math.abs(left.absChange)
    || right.grossDollarChange - left.grossDollarChange
    || left.itemName.localeCompare(right.itemName)
  ))[0] || null;

  return {
    rows,
    insights: {
      biggestItem,
      biggestVendor: vendors[0] || null,
      biggestCategory: categories[0] || null,
      vendors,
      categories,
    },
    totals: {
      itemCount: rows.length,
      increaseCount: rows.filter((row) => row.absChange > 0).length,
      decreaseCount: rows.filter((row) => row.absChange < 0).length,
      unchangedNetCount: rows.filter((row) => row.absChange === 0).length,
      totalDollarChange: rows.reduce((sum, row) => sum + row.absChange, 0),
      totalMovement: rows.reduce((sum, row) => sum + row.grossDollarChange, 0),
    },
  };
}
